<template>
  <ion-modal ref="modal" backdrop-dismiss="false" @willPresent="onPresent">
    <ion-content class="ion-padding">
      <ion-toolbar>
        <ion-buttons slot="end">
          <ion-button fill="clear" @click="dismiss()">
            <ion-icon slot="icon-only" :icon="closeOutline"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>

      <div class="callback-form">
        <h2>Upload Photos</h2>
        <p>Please upload up to twelve additional photos to support your claim.</p>

        <ion-button
          id="upload-photo-btn"
          @click="handlePhotoUpload"
          :disabled="submittedPhotos.length >= 12"
          :style="{ '--background': branding.color_primary }"
        >
          Upload Photo
        </ion-button>

        <ion-grid class="photo-grid">
          <ion-row>
            <ion-col
              v-for="photo in submittedPhotos"
              :key="photo.fileName"
              size-md="5"
              size-xs="12"
            >
              <ion-card color="light" class="photo-card">
                <ion-card-header>{{ photo.fileName }}</ion-card-header>
                <ion-button fill="clear" @click="removePhoto(photo.fileName)">
                  <ion-icon
                    slot="icon-only"
                    :icon="closeOutline"
                    color="secondary"
                  ></ion-icon>
                </ion-button>
              </ion-card>
            </ion-col>
          </ion-row>
        </ion-grid>

        <ion-button
          expand="block"
          @click="uploadAllPhotos"
          :disabled="submittedPhotos.length === 0 || isSubmitting"
          :style="{ '--background': branding.color_primary }"
        >
          <span v-if="!isSubmitting">Submit Photos</span>
          <ion-spinner v-if="isSubmitting"></ion-spinner>
        </ion-button>
      </div>
    </ion-content>
  </ion-modal>
</template>

<script setup>
import {
  IonModal,
  IonContent,
  IonToolbar,
  IonButtons,
  IonButton,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  IonCard,
  IonCardHeader,
  IonSpinner,
  toastController,
} from '@ionic/vue';
import { ref, defineProps, defineExpose } from 'vue';
import { closeOutline } from 'ionicons/icons';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { useBrandingStore } from '@/store/branding';
import { useCustomerStore } from '@/store/customer';
import { convertBlobToBase64 } from '@/composables/usePhotoGallery';
import * as Sentry from '@sentry/vue';

const customerStore = useCustomerStore();

const props = defineProps({
  policyId: Number,
});

const modal = ref(null);
const submittedPhotos = ref([]);
const branding = useBrandingStore().brandCSS;
const doneSubmitting = ref(false);
const isSubmitting = ref(false);

async function showToast(message, color = 'primary') {
  const toast = await toastController.create({
    message,
    duration: 3000,
    color,
    position: 'top',
  });
  await toast.present();
}

function dismiss() {
  if (submittedPhotos.value.length > 0 && !doneSubmitting.value) {
    const confirmed = window.confirm(
      'You have uploaded photos but not yet submitted them. Are you sure you wish to close this page?'
    );
    if (confirmed) {
      modal.value.$el.dismiss();
    }
  } else {
    modal.value.$el.dismiss();
  }
}

function removePhoto(fileName) {
  submittedPhotos.value = submittedPhotos.value.filter(
    (photo) => photo.fileName !== fileName
  );
}

async function handlePhotoUpload() {
  try {
    const photo = await Camera.getPhoto({
      resultType: CameraResultType.Uri,
      source: CameraSource.Prompt,
      quality: 90,
    });

    const photoResponse = await fetch(photo.webPath);
    const blob = await photoResponse.blob();
    const fileName = `${new Date().getTime()}.jpeg`;
    blob.fileName = fileName;

    // Convert to base64 to match backend expectations
    const base64Data = await convertBlobToBase64(blob);
    const base64WithoutPrefix = base64Data.substring(
      base64Data.indexOf(',') + 1
    );
    submittedPhotos.value.push({
      fileName,
      data: base64WithoutPrefix,
    });
  } catch (err) {
    Sentry.captureException(err);
    Sentry.captureMessage(`Photo capture error: ${err.message}`);
  }
}

async function uploadAllPhotos() {
  if (isSubmitting.value) {
    return;
  }
  isSubmitting.value = true;

  try {
    for (const photo of submittedPhotos.value) {
      try {
        await customerStore.saveAttachment(
          {
            fileName: photo.fileName,
            data: photo.data,
          },
          props.policyId
        );
      } catch (error) {
        Sentry.captureException(error);
        Sentry.captureMessage(
          `Upload error: ${error.message}, FileName: ${photo.fileName}`
        );
        await showToast(`Failed to upload ${photo.fileName}`, 'danger');
      }
    }

    await showToast('Upload complete!', 'success');
    doneSubmitting.value = true;
    dismiss();
  } finally {
    isSubmitting.value = false;
  }
}

function onPresent() {
  doneSubmitting.value = false;
  submittedPhotos.value = [];
  isSubmitting.value = false;
}

// Expose the modal ref to parent component
defineExpose({
  modal,
});
</script>

<style lang="scss" scoped>
.callback-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;

  h2 {
    margin-top: 0.5rem;
    text-align: center;
  }

  p {
    text-align: center;
    max-width: 80%;
  }
}

.photo-grid {
  padding-top: 1rem;
  width: 100%;
}

.photo-card {
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
